import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { cn, formatLevelGDID } from '~/util';
import { LevelWithExtraFilters } from '~/util/types/levels';
import { Star } from 'lucide-react';
import { nlwTierColorClasses } from '~/util/types/nlw';
import { Link } from '@tanstack/react-router';

interface LevelProps extends ComponentPropsWithoutRef<'a'> {
    level: LevelWithExtraFilters;
    idlpos?: number;
    showNlwBorder?: boolean;
    editFavorite?: (favorite: boolean) => void;
}
export const ListLevelLink = forwardRef<HTMLAnchorElement, LevelProps>(
    function LinkRow(
        { level, idlpos, showNlwBorder, editFavorite, ...props },
        ref,
    ) {
        return (
            <Link
                activeOptions={{
                    includeSearch: false,
                }}
                from="/list/$levelid"
                params={{
                    levelid: formatLevelGDID(level),
                }}
                search={(prev) => ({ ...prev, records: undefined })}
                ref={ref}
                className={cn(
                    'p-1.5 rounded-md my-0.5 border-l-8 border-transparent',
                    'group flex w-full items-center transition-colors cursor-pointer',
                    'focus-visible:bg-primary focus-visible:text-primary-foreground outline-none',
                    showNlwBorder &&
                        level.nlw_tier &&
                        nlwTierColorClasses[level.nlw_tier].border,
                    level.completed_by_user &&
                        'bg-confirm/30 hover:bg-confirm/80',
                )}
                inactiveProps={{
                    className: cn(
                        'bg-background-lighttransparent hover:bg-secondary-light',
                        showNlwBorder &&
                            level.nlw_tier &&
                            nlwTierColorClasses[level.nlw_tier]?.borderHover,
                    ),
                }}
                activeProps={{
                    className:
                        'bg-primary hover:bg-primary text-primary-foreground hover:text-primary-foreground transition-none',
                }}
                preload="intent"
                preloadDelay={500}
                resetScroll={false}
                {...props}
            >
                <p className="w-20 text-center clamp-[text-xl-2xl-clamp] lg:clamp-[text-sm-lg-clamp]">
                    {level.status == 'MainList' ? `#${level.position}` : ' - '}
                </p>
                <p className="text-start clamp-[text-xl-2xl-clamp] lg:clamp-[text-sm-lg-clamp] truncate ">
                    {level.name}{' '}
                    {idlpos && idlpos !== -1 && (
                        <span className="text-secondary-light">
                            (IDL #{idlpos})
                        </span>
                    )}
                </p>
                <Star
                    fill={level.is_favorite ? 'currentColor' : 'none'}
                    className={cn(
                        'size-6.5 ml-auto cursor-pointer transition-opacity',
                        level.is_favorite
                            ? 'opacity-80 group-hover:opacity-100'
                            : 'opacity-0 group-hover:opacity-80 hover:opacity-100',
                    )}
                    onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        editFavorite?.(!level.is_favorite);
                    }}
                />
            </Link>
        );
    },
);
ListLevelLink.displayName = 'LevelLink';
